// Unified log-period modal — writes BOTH a KpiMeasurement and a KpiReport
// per submit. Auto-RAG is computed from value + target + higherIsBetter;
// the user can override via the manual radio group. If the measurement
// succeeds but the report fails, the measurement is rolled back via the
// caller-supplied `deleteMeasurement` to keep the history table honest.

import { useMemo, useState, type FormEvent } from "react";
import Modal from "../../../../common/Modal";
import InfoHint from "../../../../common/InfoHint";
import Button from "../../../../common/Button";
import Alert from "../../../../common/Alert";
import EvidenceLinksField from "../../shared/EvidenceLinksField";
import {
  formatPeriodLabel,
  nextUnloggedPeriod,
  normaliseFrequency,
} from "../../../../../lib/kpi/period";
import {
  AMBER_BAND_RELATIVE as SHARED_AMBER_BAND_RELATIVE,
  computeAutoRag as sharedComputeAutoRag,
  parseTarget as sharedParseTarget,
} from "../../../../../lib/kpi/autoRag";
import {
  KPI_BOOLEAN_MET,
  KPI_BOOLEAN_NOT_MET,
  type CreateKpiMeasurementInput,
  type CreateKpiReportInput,
  type EvidenceLink,
  type Kpi,
  type KpiMeasurement,
  type KpiReport,
  type KpiValueType,
  type RagRating,
} from "../../../../../types";

export interface KpiLogPeriodInitialData {
  period?: string;
  value?: string;
  ragRating?: RagRating;
  commentary?: string;
  evidenceLinks?: EvidenceLink[];
  /** Existing measurement id for this period — when set, submit uses
   *  `updateMeasurement` (overwrite) instead of `submitMeasurement`
   *  (create). Fixes the amend-append-duplicate bug.  */
  measurementId?: string;
  /** Existing report id for this period — see measurementId note.  */
  reportId?: string;
}

export interface KpiLogPeriodEntryProps {
  isOpen: boolean;
  onClose: () => void;
  kpi: Kpi;
  contractId: string;
  measurements: KpiMeasurement[];
  reports: KpiReport[];
  submitMeasurement: (input: CreateKpiMeasurementInput) => Promise<{ id: string } | undefined>;
  submitReport: (input: CreateKpiReportInput) => Promise<{ id: string } | undefined>;
  /** Replace an existing measurement (amend mode). Required when
   *  `initialPeriodData.measurementId` is present. */
  updateMeasurement?: (
    id: string,
    input: CreateKpiMeasurementInput,
  ) => Promise<unknown>;
  /** Replace an existing report (amend mode). Required when
   *  `initialPeriodData.reportId` is present. */
  updateReport?: (id: string, input: CreateKpiReportInput) => Promise<unknown>;
  /** Called to roll back a newly-created measurement id when the
   *  subsequent report write fails (create flow only). */
  deleteMeasurement?: (id: string) => Promise<void>;
  initialPeriodData?: KpiLogPeriodInitialData;
  now?: Date;
}

// Auto-RAG helpers live in `lib/kpi/autoRag.ts` — same logic shared
// with BulkPeriodEntryModal to keep the tie-case + amber band rules
// in exactly one place. Re-exported here for any downstream
// test / component still importing from this module.
const parseTarget = sharedParseTarget;
const computeAutoRag = sharedComputeAutoRag;
export const AMBER_BAND_RELATIVE = SHARED_AMBER_BAND_RELATIVE;

// Evidence-link cap mirrors the server-side limit in
// backend/src/models/tracking.ts (LinksArraySchema). When the server
// rejects with a recognisable cap error, we rewrite it to the
// user-actionable "Too many evidence links (max 20). Remove N link(s)…"
// form — same pattern the retired KpiReportEntry used.
const EVIDENCE_CAP = 20;

function isCapError(message: string): boolean {
  return /evidenceLinks|max .{0,10}element|at most 20/i.test(message);
}

function formatCapError(overage: number): string {
  const plural = overage === 1 ? "" : "s";
  return `Too many evidence links (max ${EVIDENCE_CAP}). Remove ${overage} link${plural} before saving.`;
}

const RAG_OPTIONS: RagRating[] = ["red", "amber", "green"];
const BOOLEAN_AMBER_TOOLTIP = "Met/Not-met KPIs use Green or Red only";

export default function KpiLogPeriodEntry({
  isOpen,
  onClose,
  kpi,
  contractId,
  measurements,
  reports,
  submitMeasurement,
  submitReport,
  updateMeasurement,
  updateReport,
  deleteMeasurement,
  initialPeriodData,
  now,
}: KpiLogPeriodEntryProps) {
  const isAmendMode = Boolean(initialPeriodData);
  const amendingMeasurementId = initialPeriodData?.measurementId;
  const amendingReportId = initialPeriodData?.reportId;

  const defaultPeriod = useMemo(() => {
    if (initialPeriodData?.period) return initialPeriodData.period;
    return nextUnloggedPeriod(kpi, measurements, reports, now);
  }, [kpi, measurements, reports, initialPeriodData, now]);

  const [period, setPeriod] = useState(defaultPeriod);
  const [value, setValue] = useState(initialPeriodData?.value ?? "");
  const [manualRag, setManualRag] = useState<RagRating | null>(
    initialPeriodData?.ragRating ?? null,
  );
  const [commentary, setCommentary] = useState(
    initialPeriodData?.commentary ?? "",
  );
  const [evidenceLinks, setEvidenceLinks] = useState<EvidenceLink[]>(
    initialPeriodData?.evidenceLinks ?? [],
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Pattern 0: seed form state during render when the modal opens for a new
  // target session. The key encodes the KPI id + amend period so re-opens for
  // different periods or KPIs always seed fresh, while mid-session React Query
  // refetches leave the user's in-progress edits untouched.
  const seedKey = isOpen
    ? `${kpi.id}:${initialPeriodData?.period ?? "new"}:${initialPeriodData?.measurementId ?? ""}`
    : null;
  const [seededFor, setSeededFor] = useState<string | null>(null);
  if (seedKey !== null && seedKey !== seededFor) {
    setSeededFor(seedKey);
    setPeriod(defaultPeriod);
    setValue(initialPeriodData?.value ?? "");
    // Amend-amber guard: a legacy amber report on a KPI whose valueType was
    // later flipped to boolean would seed manualRag="amber" with the amber
    // radio disabled, creating an un-unselectable checked state that 400s on
    // save. Force null so the user picks green/red from scratch. Historical
    // display-only amber rows are unaffected.
    const seededRag = initialPeriodData?.ragRating ?? null;
    const isBooleanKpi = (kpi.valueType ?? "percentage") === "boolean";
    setManualRag(isBooleanKpi && seededRag === "amber" ? null : seededRag);
    setCommentary(initialPeriodData?.commentary ?? "");
    setEvidenceLinks(initialPeriodData?.evidenceLinks ?? []);
    setErrors({});
    setSubmitError(null);
    setSubmitting(false);
  }

  const autoRag = useMemo(() => computeAutoRag(value, kpi), [value, kpi]);
  const effectiveRag: RagRating | null = manualRag ?? autoRag;
  const isManualOverride =
    manualRag !== null && autoRag !== null && manualRag !== autoRag;

  const targetParse = useMemo(() => parseTarget(kpi.target), [kpi.target]);
  // Adaptive value input. Legacy KPIs without `valueType`
  // default to "percentage" to match pre-PR-B behaviour exactly.
  const valueType: KpiValueType = kpi.valueType ?? "percentage";
  // Suffix precedence: explicit `kpi.unit` on number KPIs, else whatever
  // the target string happens to carry (covers legacy KPIs where the
  // target encodes the unit inline — e.g. "<4 hrs").
  const valueSuffix =
    valueType === "number" ? (kpi.unit ?? "") : (targetParse?.suffix ?? "");
  // Boolean KPIs never auto-amber (enforced upstream in autoRag.ts) and
  // the backend rejects amber reports against boolean KPIs outright —
  // disable the amber radio to prevent the user sending a 400.
  const amberDisabled = valueType === "boolean";

  const amendFrequency = normaliseFrequency(kpi.measurementFrequency);
  const amendPeriodKey = initialPeriodData?.period ?? period;
  const title = isAmendMode
    ? `Amend ${formatPeriodLabel(amendPeriodKey, amendFrequency)}`
    : `Log period — ${kpi.name}`;

  if (!isOpen) return null;

  const ragChanged =
    isAmendMode &&
    manualRag !== null &&
    initialPeriodData?.ragRating !== undefined &&
    manualRag !== initialPeriodData.ragRating;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const next: Record<string, string> = {};
    const trimmedPeriod = period.trim();
    if (!trimmedPeriod) next.period = "Period is required";
    if (!value.trim()) next.value = "Value is required";
    if (!effectiveRag) next.rag = "RAG rating is required";
    // Block writing a second entry for a period that's already logged.
    // Amend mode skips the check for the period it's amending (same
    // measurementId / reportId = overwrite, not duplicate). Users with
    // a genuine new period should clear the field and type another.
    if (trimmedPeriod && !isAmendMode) {
      const dupeMeasurement = measurements.some(
        (m) => m.period === trimmedPeriod,
      );
      const dupeReport = reports.some((r) => r.period === trimmedPeriod);
      if (dupeMeasurement || dupeReport) {
        next.period =
          "This period already has a logged entry. Use the pencil icon on the history row to amend it instead.";
      }
    }
    // PO scenario B: when amending a period and changing the RAG,
    // commentary is required so the audit trail captures the reason.
    if (ragChanged && !commentary.trim()) {
      next.commentary = "Provide a reason for changing the RAG rating.";
    }
    // Evidence label is required when the period has more than one link
    // so reviewers can tell them apart. Single-link rows may leave the
    // label blank — the URL is enough on its own.
    if (evidenceLinks.length > 1) {
      const missingLabel = evidenceLinks.some(
        (l) => l.url.trim() !== "" && l.label.trim() === "",
      );
      if (missingLabel) {
        next.evidence =
          "Give each link a short name so reviewers can tell them apart (e.g. 'March dashboard screenshot').";
      }
    }
    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }
    setErrors({});
    setSubmitError(null);
    setSubmitting(true);

    const trimmedValue = value.trim();
    const valueChanged =
      !isAmendMode ||
      (initialPeriodData?.value ?? "").trim() !== trimmedValue;

    const mInput: CreateKpiMeasurementInput = {
      contractId,
      name: `${kpi.name} measurement`,
      parentId: kpi.id,
      period: period.trim(),
      value: trimmedValue,
      date: new Date().toISOString().slice(0, 10),
    };
    const rInput: CreateKpiReportInput = {
      contractId,
      name: `${kpi.name} report`,
      parentId: kpi.id,
      period: period.trim(),
      ragRating: effectiveRag as RagRating,
    };
    if (commentary.trim()) rInput.commentary = commentary.trim();
    if (evidenceLinks.length > 0) rInput.evidenceLinks = evidenceLinks;

    // Track what we just *created* so we can roll back if a later step
    // fails. We only roll back creates — updates are best-effort restored
    // by re-posting a snapshot (not implemented; see rollback branch).
    let newlyCreatedMeasurementId: string | null = null;
    try {
      // Measurement path: update if an existing id is present AND value
      // changed; otherwise skip. Create if no existing id exists.
      if (amendingMeasurementId) {
        if (valueChanged) {
          if (!updateMeasurement) {
            throw new Error(
              "updateMeasurement handler missing — cannot amend.",
            );
          }
          await updateMeasurement(amendingMeasurementId, mInput);
        }
      } else if (valueChanged || !isAmendMode) {
        const m = await submitMeasurement(mInput);
        newlyCreatedMeasurementId = m?.id ?? null;
      }

      // Report path: update if an existing id is present; else create.
      if (amendingReportId) {
        if (!updateReport) {
          throw new Error("updateReport handler missing — cannot amend.");
        }
        await updateReport(amendingReportId, rInput);
      } else {
        await submitReport(rInput);
      }
      onClose();
    } catch (err) {
      const rawMsg = err instanceof Error ? err.message : "Failed to save.";
      // Map the server-side evidenceLinks cap error to a user-actionable
      // form error rather than the raw server message (matches the
      // existing KpiReportEntry.isCapError pattern, since retired).
      const capOverage =
        evidenceLinks.length > EVIDENCE_CAP && isCapError(rawMsg)
          ? evidenceLinks.length - EVIDENCE_CAP
          : null;
      const msg =
        capOverage !== null ? formatCapError(capOverage) : rawMsg;
      setSubmitError(msg);
      if (newlyCreatedMeasurementId && deleteMeasurement) {
        try {
          await deleteMeasurement(newlyCreatedMeasurementId);
        } catch (rollbackErr) {
          // Surface the partial-write failure.
          // Don't swallow — the user needs to know they have orphaned
          // data and a retry would create a second duplicate.
          const rbMsg =
            rollbackErr instanceof Error
              ? rollbackErr.message
              : "unknown error";
          setSubmitError(
            `${msg}\n\nMeasurement was saved but the report failed, and ` +
              `rollback also failed (${rbMsg}). Please contact support to ` +
              `clean up before retrying, or the next submit will create a ` +
              `duplicate measurement.`,
          );
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      titleAccessory={<InfoHint text={kpi.definition ?? ""} />}
      maxWidth="sm:max-w-xl"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {isAmendMode && (
          <Alert variant="warning">
            Submitting will replace the existing records for this period.
          </Alert>
        )}

        <div>
          <div className="label">
            Period{!isAmendMode && <span className="text-red-600"> *</span>}
          </div>
          {isAmendMode ? (
            // Amend mode locks the period — editing here would orphan the
            // amended record and create a duplicate at the new period.
            // Render as static text rather than a disabled input so users
            // don't try to click and type.
            <p
              className="text-body text-slate-900 dark:text-slate-100"
              data-testid="logperiod-period-readonly"
            >
              {period}
            </p>
          ) : (
            <>
              <label htmlFor="logperiod-period" className="sr-only">
                Period
              </label>
              <input
                id="logperiod-period"
                className="input"
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                disabled={submitting}
                maxLength={50}
                aria-invalid={!!errors.period}
              />
              {errors.period && (
                <p className="mt-1 text-caption text-red-600 dark:text-red-400">
                  {errors.period}
                </p>
              )}
            </>
          )}
        </div>

        {valueType === "boolean" ? (
          <fieldset aria-invalid={!!errors.value}>
            <legend className="label">
              Value <span className="text-red-600">*</span>
            </legend>
            <div className="flex flex-wrap gap-3" data-testid="value-boolean">
              {(
                [
                  { value: KPI_BOOLEAN_MET, label: "Met" },
                  { value: KPI_BOOLEAN_NOT_MET, label: "Not met" },
                ] as const
              ).map((opt) => (
                <label
                  key={opt.value}
                  className="inline-flex items-center gap-2 text-caption text-slate-700 dark:text-slate-300"
                >
                  <input
                    type="radio"
                    name="logperiod-value"
                    className="accent-brand-600"
                    value={opt.value}
                    checked={value === opt.value}
                    onChange={() => {
                      setValue(opt.value);
                      // Clear a stale manual override when the value flips
                      // — the auto-RAG will recompute green/red and we
                      // don't want an orphaned amber selection sneaking
                      // through (defensive; the radio also disables it).
                      if (manualRag === "amber") setManualRag(null);
                    }}
                    disabled={submitting}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
            {errors.value && (
              <p className="mt-1 text-caption text-red-600 dark:text-red-400">
                {errors.value}
              </p>
            )}
          </fieldset>
        ) : (
          <div>
            <label htmlFor="logperiod-value" className="label">
              Value <span className="text-red-600">*</span>
            </label>
            <div className="flex items-center gap-2">
              <input
                id="logperiod-value"
                className="input flex-1"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                disabled={submitting}
                inputMode="decimal"
                aria-invalid={!!errors.value}
              />
              {valueSuffix && (
                <span
                  data-testid="value-unit-hint"
                  className="text-caption text-slate-500 dark:text-slate-400"
                >
                  {valueSuffix}
                </span>
              )}
            </div>
            {errors.value && (
              <p className="mt-1 text-caption text-red-600 dark:text-red-400">
                {errors.value}
              </p>
            )}
          </div>
        )}

        <fieldset aria-invalid={!!errors.rag}>
          <legend className="label">
            RAG rating <span className="text-red-600">*</span>
          </legend>
          <div className="flex flex-wrap gap-3">
            {RAG_OPTIONS.map((r) => {
              const disabled = submitting || (amberDisabled && r === "amber");
              return (
                <label
                  key={r}
                  className={`inline-flex items-center gap-2 text-caption ${
                    disabled && amberDisabled && r === "amber"
                      ? "text-slate-400 dark:text-slate-500"
                      : "text-slate-700 dark:text-slate-300"
                  }`}
                  title={
                    amberDisabled && r === "amber"
                      ? BOOLEAN_AMBER_TOOLTIP
                      : undefined
                  }
                >
                  <input
                    type="radio"
                    name="logperiod-rag"
                    className="accent-brand-600"
                    value={r}
                    checked={effectiveRag === r}
                    onChange={() => setManualRag(r)}
                    disabled={disabled}
                    aria-label={
                      amberDisabled && r === "amber"
                        ? `Amber (${BOOLEAN_AMBER_TOOLTIP})`
                        : undefined
                    }
                  />
                  {r.charAt(0).toUpperCase() + r.slice(1)}
                </label>
              );
            })}
          </div>
          {isManualOverride && autoRag && (
            <p className="mt-1 text-caption text-slate-500 dark:text-slate-400">
              Manually overridden — auto-calculated was{" "}
              {autoRag.charAt(0).toUpperCase() + autoRag.slice(1)}
            </p>
          )}
          {errors.rag && (
            <p className="mt-1 text-caption text-red-600 dark:text-red-400">
              {errors.rag}
            </p>
          )}
        </fieldset>

        <div>
          <label htmlFor="logperiod-commentary" className="label">
            Commentary
            {ragChanged && <span className="text-red-600"> *</span>}
          </label>
          <textarea
            id="logperiod-commentary"
            className="input min-h-[80px]"
            value={commentary}
            onChange={(e) => setCommentary(e.target.value)}
            disabled={submitting}
            maxLength={2000}
            aria-invalid={!!errors.commentary}
          />
          {errors.commentary && (
            <p
              className="mt-1 text-caption text-red-600 dark:text-red-400"
              data-testid="commentary-required-error"
            >
              {errors.commentary}
            </p>
          )}
        </div>

        <div>
          <EvidenceLinksField
            value={evidenceLinks}
            onChange={setEvidenceLinks}
            disabled={submitting}
          />
          {errors.evidence && (
            <p
              className="mt-1 text-caption text-red-600 dark:text-red-400"
              data-testid="evidence-label-error"
            >
              {errors.evidence}
            </p>
          )}
          {evidenceLinks.length >= 10 && evidenceLinks.length < 18 && (
            <p
              className="mt-1 text-caption text-slate-500 dark:text-slate-400"
              data-testid="evidence-nudge-10"
            >
              Consider grouping related evidence into a single SharePoint
              folder for clarity.
            </p>
          )}
          {evidenceLinks.length >= 18 && evidenceLinks.length <= 20 && (
            <p
              className="mt-1 text-caption text-amber-600 dark:text-amber-400"
              data-testid="evidence-nudge-18"
            >
              {20 - evidenceLinks.length} of 20 remaining.
            </p>
          )}
        </div>

        {submitError && <Alert variant="error">{submitError}</Alert>}

        <div className="flex justify-end gap-3 pt-2">
          <Button
            variant="secondary"
            type="button"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button type="submit" loading={submitting} disabled={submitting}>
            Save
          </Button>
        </div>
      </form>
    </Modal>
  );
}
